import React, { useState, useEffect } from 'react';
import { X, Save, Loader, AlertTriangle } from 'lucide-react';
import { useClerk, useOrganization } from '@clerk/clerk-react';
import { getOrgSlug, getApiBaseUrl } from '../config/backend.config';
import './WorkspaceInstructionsModal.css';

function WorkspaceInstructionsModal({ isOpen, onClose, selectedWorkspace }) {
  const [instructions, setInstructions] = useState('');
  const [originalInstructions, setOriginalInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const clerk = useClerk();
  const { organization } = useOrganization();

  // Fetch current instructions when modal opens
  useEffect(() => {
    if (isOpen && selectedWorkspace) {
      fetchInstructions();
    } else {
      // Reset state when modal closes
      setInstructions('');
      setOriginalInstructions('');
      setError(null);
    }
  }, [isOpen, selectedWorkspace]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInstructions = async () => {
    if (!clerk?.session || !organization || !selectedWorkspace) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        throw new Error('Unable to get authentication token');
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(
        `${apiBaseUrl}/workspaces/${selectedWorkspace.workspace_id}/instructions`,
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Only workspace owner can view instructions');
        }
        throw new Error(`Failed to fetch instructions: ${response.statusText}`);
      }

      const data = await response.json();
      const fetchedInstructions = data.instructions || '';
      setInstructions(fetchedInstructions);
      setOriginalInstructions(fetchedInstructions);
    } catch (err) {
      console.error('Error fetching instructions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!clerk?.session || !organization || !selectedWorkspace) {
      return;
    }

    // Check if instructions actually changed
    if (instructions !== originalInstructions) {
      const confirmed = window.confirm(
        'Saving instructions will restart the agent session. Your current conversation will be cleared and a new agent will be created with the updated instructions.\n\nDo you want to continue?'
      );
      if (!confirmed) {
        return;
      }
    }

    setSaving(true);
    setError(null);

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        throw new Error('Unable to get authentication token');
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(
        `${apiBaseUrl}/workspaces/${selectedWorkspace.workspace_id}/instructions`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            instructions: instructions
          })
        }
      );

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Only workspace owner can set instructions');
        }
        throw new Error(`Failed to save instructions: ${response.statusText}`);
      }

      // Close modal on success
      onClose();
    } catch (err) {
      console.error('Error saving instructions:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="workspace-instructions-overlay" onClick={onClose}>
      <div className="workspace-instructions-modal" onClick={(e) => e.stopPropagation()}>
        <div className="workspace-instructions-header">
          <h2>Workspace Instructions</h2>
          <button
            className="workspace-instructions-close"
            onClick={onClose}
            title="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="workspace-instructions-body">
          <p className="workspace-instructions-description">
            Add custom instructions that will be included in the system prompt for this workspace.
            These instructions will guide the AI agent's behavior and responses.
          </p>

          {error && (
            <div className="workspace-instructions-error">
              <AlertTriangle size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> {error}
            </div>
          )}

          {loading ? (
            <div className="workspace-instructions-loading">
              <Loader size={20} className="spinner" />
              <span>Loading instructions...</span>
            </div>
          ) : (
            <textarea
              className="workspace-instructions-textarea"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Enter workspace instructions here...&#10;&#10;Example:&#10;- Always use metric units in responses&#10;- Prefer visualizations over tables when possible&#10;- Include confidence intervals in statistical analysis"
              rows={15}
            />
          )}
        </div>

        <div className="workspace-instructions-footer">
          <button
            className="workspace-instructions-cancel"
            onClick={onClose}
            disabled={saving || loading}
          >
            Cancel
          </button>
          <button
            className="workspace-instructions-save"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? (
              <>
                <Loader size={16} className="spinner" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Instructions
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default WorkspaceInstructionsModal;

